import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  ButtonBase,
  Button,
  Collapse,
  LinearProgress,
  Fade,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ExpandMore,
  ExpandLess,
  Close,
  RocketLaunch,
  Architecture,
  Chat,
  Checklist,
  Description,
  VerifiedUser,
  AccountTree,
  LibraryBooks,
  Celebration,
  ArrowForward,
  Replay,
} from '@mui/icons-material';
import { useOnboarding } from '../../context/OnboardingContext';

const ICON_MAP = {
  RocketLaunch: <RocketLaunch sx={{ fontSize: 18 }} />,
  Architecture: <Architecture sx={{ fontSize: 18 }} />,
  Chat: <Chat sx={{ fontSize: 18 }} />,
  Checklist: <Checklist sx={{ fontSize: 18 }} />,
  Description: <Description sx={{ fontSize: 18 }} />,
  VerifiedUser: <VerifiedUser sx={{ fontSize: 18 }} />,
  LibraryBooks: <LibraryBooks sx={{ fontSize: 18 }} />,
  AccountTree: <AccountTree sx={{ fontSize: 18 }} />,
};

export default function OnboardingChecklist({ onNavigate }) {
  const {
    steps,
    completedCount,
    totalSteps,
    allComplete,
    loaded,
    dismissed,
    activeGuide,
    nextStep,
    isStepComplete,
    startGuide,
    // eslint-disable-next-line no-unused-vars -- retained (WIP)
    stopGuide,
    dismiss,
    skipAll,
    replayStep,
  } = useOnboarding();

  const [expanded, setExpanded] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [justCompleted, setJustCompleted] = useState(null); // flash effect
  const prevCountRef = useRef(0);

  // Flash effect when a step completes, then auto-advance guide to next step
  useEffect(() => {
    if (completedCount > prevCountRef.current && prevCountRef.current > 0) {
      // Find which step just completed
      const completed = steps.find(s => isStepComplete(s.id) && !justCompleted);
      if (completed) {
        setJustCompleted(completed.id);
        const timer = setTimeout(() => setJustCompleted(null), 1500);
        return () => clearTimeout(timer);
      }
    }
    prevCountRef.current = completedCount;
  }, [completedCount, steps, isStepComplete, justCompleted]);

  // Show celebration when all steps complete
  useEffect(() => {
    if (allComplete && completedCount === totalSteps) {
      setShowCelebration(true);
      const timer = setTimeout(() => setShowCelebration(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [allComplete, completedCount, totalSteps]);

  // Don't render until loaded, or if dismissed
  if (!loaded || dismissed) return null;

  const progressPct = (completedCount / totalSteps) * 100;

  const handleStepClick = (step) => {
    if (isStepComplete(step.id)) return; // Already done
    // Navigate to the section
    if (step.section && onNavigate) {
      onNavigate(step.section);
    }
    // Activate the spotlight guide (slight delay to let section render)
    setTimeout(() => startGuide(step.id), 300);
  };

  const handleReplay = (step) => {
    if (step.section && onNavigate) {
      onNavigate(step.section);
    }
    setTimeout(() => replayStep(step.id), 300);
  };

  return (
    <Fade in>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          width: 340,
          maxHeight: expanded ? 'calc(100vh - 120px)' : 'auto',
          borderRadius: 3,
          overflow: 'hidden',
          zIndex: 1200,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: allComplete
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            transition: 'background 0.5s',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {allComplete ? (
              <Celebration sx={{ color: 'white', fontSize: 20 }} />
            ) : (
              <RocketLaunch sx={{ color: 'white', fontSize: 20 }} />
            )}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                {allComplete ? 'Setup Complete!' : 'Getting Started'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {completedCount} of {totalSteps} steps done
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); dismiss(); }}
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}
            >
              <Tooltip title="Minimize checklist">
                <Close sx={{ fontSize: 16 }} />
              </Tooltip>
            </IconButton>
            {expanded ? (
              <ExpandMore sx={{ color: 'white', fontSize: 20 }} />
            ) : (
              <ExpandLess sx={{ color: 'white', fontSize: 20 }} />
            )}
          </Box>
        </Box>

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 3,
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            '& .MuiLinearProgress-bar': {
              background: allComplete
                ? 'linear-gradient(90deg, #11998e, #38ef7d)'
                : 'linear-gradient(90deg, #667eea, #764ba2)',
              transition: 'transform 0.6s ease',
            },
          }}
        />

        {/* Celebration banner */}
        <Collapse in={showCelebration && allComplete}>
          <Box sx={{
            px: 2, py: 1.5,
            bgcolor: 'rgba(56, 239, 125, 0.08)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#11998e' }}>
              You're all set! You've explored the key features.
            </Typography>
          </Box>
        </Collapse>

        {/* Step list */}
        <Collapse in={expanded}>
          <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            {steps.map((step, index) => {
              const done = isStepComplete(step.id);
              const isNext = nextStep?.id === step.id;
              const isActive = activeGuide === step.id;
              const wasJustCompleted = justCompleted === step.id;

              return (
                <ButtonBase
                  key={step.id}
                  onClick={() => done ? handleReplay(step) : handleStepClick(step)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    width: '100%',
                    textAlign: 'left',
                    borderBottom: index < steps.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: wasJustCompleted
                      ? 'rgba(17, 153, 142, 0.08)'
                      : isActive
                        ? 'rgba(102, 126, 234, 0.1)'
                        : isNext
                          ? 'rgba(102, 126, 234, 0.04)'
                          : 'transparent',
                    opacity: done && !wasJustCompleted ? 0.75 : 1,
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: done ? 'rgba(17, 153, 142, 0.06)' : 'rgba(102, 126, 234, 0.08)',
                    },
                  }}
                >
                  {/* Check icon */}
                  <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                    {done ? (
                      <CheckCircle sx={{
                        fontSize: 20,
                        color: '#11998e',
                        transition: 'transform 0.3s',
                        transform: wasJustCompleted ? 'scale(1.3)' : 'scale(1)',
                      }} />
                    ) : (
                      <RadioButtonUnchecked sx={{
                        fontSize: 20,
                        color: isActive ? '#667eea' : isNext ? '#667eea' : 'text.disabled',
                      }} />
                    )}
                  </Box>

                  {/* Icon + text */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ color: done ? '#11998e' : isActive ? '#667eea' : isNext ? '#667eea' : 'text.secondary' }}>
                        {ICON_MAP[step.icon]}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive || isNext ? 600 : 500,
                          textDecoration: done ? 'line-through' : 'none',
                          color: done ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        {step.label}
                      </Typography>
                      {isActive && (
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(102, 126, 234, 0.15)',
                            color: '#667eea',
                          }}
                        />
                      )}
                      {!isActive && isNext && !done && (
                        <Chip
                          label="Next"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(102, 126, 234, 0.12)',
                            color: '#667eea',
                          }}
                        />
                      )}
                      {wasJustCompleted && (
                        <Chip
                          label="Done!"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(17, 153, 142, 0.15)',
                            color: '#11998e',
                          }}
                        />
                      )}
                    </Box>
                    {!done && (
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                        {step.description}
                      </Typography>
                    )}
                  </Box>

                  {/* Arrow / Replay icon */}
                  {done ? (
                    <Tooltip title="Repeat this step">
                      <Replay sx={{ fontSize: 16, color: 'text.disabled', mt: 0.5, '&:hover': { color: '#667eea' } }} />
                    </Tooltip>
                  ) : (
                    <ArrowForward sx={{ fontSize: 16, color: 'text.disabled', mt: 0.5 }} />
                  )}
                </ButtonBase>
              );
            })}
          </Box>

          {/* Footer with Skip + Complete */}
          <Box sx={{
            px: 2, py: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: allComplete ? 'center' : 'space-between',
            alignItems: 'center',
          }}>
            {allComplete ? (
              <ButtonBase
                onClick={dismiss}
                sx={{
                  px: 3, py: 0.75,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': { opacity: 0.9 },
                }}
              >
                Complete Setup
              </ButtonBase>
            ) : (
              <>
                <Button
                  size="small"
                  onClick={skipAll}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}
                >
                  Skip setup
                </Button>
                {nextStep && (
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                    onClick={() => handleStepClick(nextStep)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      borderColor: '#667eea',
                      color: '#667eea',
                      '&:hover': { borderColor: '#764ba2', bgcolor: 'rgba(102, 126, 234, 0.04)' },
                    }}
                  >
                    {nextStep.label}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  );
}
